// ============================================================================
// « IMPORTER UN FICHIER » — la troisième porte de création d'articles, transposée de la maquette (cinq étapes :
// Fichier · Colonnes · Doublons · Destination · Rapport).
//
// ⚠️ LE FICHIER EST LU DANS LE NAVIGATEUR, et rien ne part avant le lancement. Ce n'est pas une commodité : un fichier
// fournisseur contient des prix d'achat, donc des conditions négociées. Le téléverser pour l'analyser en ferait une
// copie sur le serveur avant que quiconque ait dit oui.
//
// ⚠️ LE .XLSX EST LU PAR SHEETJS 0.20.3 (Apache-2.0), chargée à la DEMANDE (`await import`) : elle pèse plus que tout
// le reste de l'écran, et la plupart des sessions n'importent aucun fichier. Les options de lecture sont celles de la
// maquette — voir `lireClasseur`.
//
// ⚠️ PAS DE « FICHIER D'EXEMPLE » (la maquette en propose un) : elle n'écrit rien, elle peut se le permettre ; ici
// l'assistant crée de vrais articles dans une vraie base.
//
// ⚠️ UNE LIGNE SANS CODE-BARRES N'EST PAS ÉCARTÉE (décision utilisateur) : le serveur la rapproche d'un article existant
// par son code article, sa couleur et sa taille, ou lui attribue un gencode INTERNE ; un code article absent est
// proposé d'après la désignation (`proposeArticleCode`, la règle des écrans de création).
//
// ⚠️ L'IMPORT SE LANCE EN ARRIÈRE-PLAN (maquette) : la fenêtre se ferme, `import-job.ts` porte l'envoi, et une
// notification en bas à droite dit ce que le serveur a RÉELLEMENT fait — jamais un pourcentage inventé.
// ============================================================================
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { checkLabel, confirm, Icon, Modal, OZ_INTERACTIVE_SURFACE, Picker, rovingRadio, rovingTab, showToast, Steps, Trans, useLatest } from '@orgazone/ui-web';
import { normalizeCode, typedDocumentNumber } from '@orgazone/sync-contract';
import {
  api, CREATE_FAMILY, type ImportArticlesOptions, type ImportDestinationKind, type ImportFamilies, type ImportMask,
  type ImportOpenDocument, type SizeGridOption,
} from '../api';
import { key, nombre, t } from '../i18n';
import type { IconName } from '@orgazone/ui-tokens';
import { analyserImport } from './import-analyse';
import { useAuth } from '../auth';
import { estClasseur, lireClasseur, lireCsv, ressembleAUnEntete, type FichierLu } from './import-lecture';
import {
  appliquerMasque, champLabel, champsManquants, CHAMPS_IMPORT, colonnesRetenues, proposerMasque, type Masque,
} from './import-masque';
import { launchImport, useImportJob } from './import-job';
import { fieldLabel } from '../vocabulary';
import { currentLang } from '../lang';

/** Les cinq étapes de la maquette — la cinquième montre ce qui partira, puis lance. */
export const ETAPES_IMPORT = [
  key('Fichier'), key('Colonnes'), key('Doublons'), key('Destination'), key('Rapport'),
] as const;

/** Les extensions RÉELLEMENT lues — la zone de dépôt n'annonce que celles-là. */
const EXTENSIONS_LUES = '.csv,.xlsx,.xlsm,.xlsb,.xls,text/csv';

/** Plafond d'un envoi — le même que le serveur : au-delà, il refuserait, autant le dire avant. */
const IMPORT_ROWS_MAX = 5000;

/** Ce qu'on retient d'un fichier déposé — y compris ses OCTETS, pour relire une autre feuille sans le redemander. */
interface FichierCharge {
  nom: string;
  taille: string;
  octets: ArrayBuffer;
  lu: FichierLu;
  hasHeading: boolean;
}

/** « 12,4 ko » — lisible, et sans décimale au-delà du méga où elle n'apprend plus rien. */
const poids = (n: number): string => (n < 1024 ? t('{v} o', { v: nombre(n) })
  : n < 1024 * 1024 ? t('{v} ko', { v: nombre(Math.round(n / 102.4) / 10) })
    : t('{v} Mo', { v: Math.round(n / (1024 * 1024)) }));

/** Les quatre destinations des quantités — celles de la maquette, dans son ordre. */
const DESTINATIONS: readonly { k: ImportDestinationKind; title: ReturnType<typeof key>; note: ReturnType<typeof key>; icon: IconName }[] = [
  { k: 'bl', title: key('Bon de livraison'), note: key('À contrôler puis entrer en stock.'), icon: 'truck' },
  { k: 'cmd', title: key('Commande fournisseur'), note: key('Attendue, pas encore reçue.'), icon: 'file' },
  { k: 'stock', title: key('Entrée directe en stock'), note: key('Disponible immédiatement.'), icon: 'lidBox' },
  { k: 'ref', title: key('Articles seulement'), note: key('Aucun mouvement de stock.'), icon: 'layers' },
];

/**
 * LE JOUR D'UN DOCUMENT dans la liste « Rattacher à un document existant » — « 12 juil. », dans la langue du compte.
 * ⚠️ LU AU RENDU (`currentLang()`) : la langue du compte n'est connue qu'après le chargement du module.
 */
const jourDuDocument = (ms: number): string =>
  new Intl.DateTimeFormat(currentLang(), { day: 'numeric', month: 'short' }).format(new Date(ms));

/** Où en est la résolution d'une famille du fichier — le badge de la colonne « État ». */
type FamilleEtat = { kind: 'learned' | 'exact' | 'unknown'; target: string } | { kind: 'near'; target: string; pct: number };

/**
 * D'OÙ VIENT LA FAMILLE PROPOSÉE pour un libellé du fichier : apprise pour ce fournisseur, identique à une famille du
 * magasin, proche d'une famille (sans être appliquée), ou à définir.
 * ⚠️ UNE PROXIMITÉ N'EST JAMAIS APPLIQUÉE SEULE (cadrage des graphies : « une hypothèse présentée comme une certitude
 *    ferait fusionner deux familles réelles ») : elle se signale, l'opérateur choisit.
 */
function resoudreFamille(raw: string, data: ImportFamilies): FamilleEtat {
  const norm = normalizeCode(raw);
  const appris = data.learned.find((l) => normalizeCode(l.alias) === norm);
  if (appris && data.families.some((f) => f.code === appris.familyCode)) return { kind: 'learned', target: appris.familyCode };
  const verdict = checkLabel(raw, data.families.map((f) => f.label));
  const parLibelle = (label: string) => data.families.find((f) => f.label === label)?.code ?? '';
  if (verdict.etat === 'exact') return { kind: 'exact', target: parLibelle(verdict.candidat) };
  if (verdict.etat === 'proche') return { kind: 'near', target: parLibelle(verdict.candidat), pct: verdict.pourcent };
  return { kind: 'unknown', target: '' };
}

/** Ce que la famille du fichier rejoindra : le choix de l'opérateur, sinon l'apprise ou l'identique — jamais la proche. */
const familleRetenue = (raw: string, choix: Record<string, string>, etat: FamilleEtat): string =>
  choix[raw] ?? (etat.kind === 'learned' || etat.kind === 'exact' ? etat.target : '');

export function AssistantImport({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [etape, setEtape] = useState(1);
  const [fichier, setFichier] = useState<FichierCharge | null>(null);
  /**
   * LA CORRESPONDANCE COLONNE → CHAMP.
   * ⚠️ RECALCULÉE quand la lecture change (autre feuille, bascule d'en-tête) : les colonnes ne sont plus les mêmes,
   *    et garder l'ancienne enverrait le prix d'achat dans le prix de vente.
   */
  const [masque, setMask] = useState<Masque>({});
  /** Les masques déjà mémorisés. ⚠️ Best-effort ASSUMÉ : une lecture en échec laisse la liste vide, on affecte à la main. */
  const [masks, setMasks] = useState<ImportMask[]>([]);
  const latestMasks = useLatest();
  const reloadMasks = useCallback(() => {
    const isLatest = latestMasks();
    api.importMasks()
      .then((r) => { if (isLatest()) setMasks(r.masks); })
      .catch(() => { if (isLatest()) setMasks([]); });
  }, [latestMasks]);
  useEffect(() => { if (open) reloadMasks(); }, [open, reloadMasks]);
  const [lecture, setReadOnly] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  /**
   * LE FOURNISSEUR DU FICHIER — choisi dans le bloc du masque (maquette), rappelé avec lui. C'est le sien qui apprend
   * les correspondances de familles et à qui vont le bon ou la commande.
   */
  const [fournisseur, setFournisseur] = useState('');
  const [fournisseurs, setFournisseurs] = useState<string[] | null>(null);
  const [fournisseursKo, setFournisseursKo] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    let vivant = true;
    api.referentielSuppliers()
      .then((r) => { if (vivant) { setFournisseurs(r.rows.map((s) => s.label)); setFournisseursKo(null); } })
      .catch((e: unknown) => { if (vivant) setFournisseursKo(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [open]);

  /** L'analyse du fichier sous le masque — PURE, recalculée à chaque changement de l'un ou l'autre. */
  const analyse = useMemo(() => (fichier ? analyserImport(fichier.lu.rows, masque, fichier.hasHeading, fichier.lu.lines) : null), [fichier, masque]);
  const mapped = (k: string) => Object.values(masque).includes(k);

  // ── ÉTAPE 3 : DOUBLONS, FAMILLES, GRILLE ───────────────────────────────────────────────────────
  /** « Ignorer la ligne » est le choix RECOMMANDÉ de la maquette, donc le défaut. */
  const [doublons, setDoublons] = useState<'skip' | 'update'>('skip');
  const [familles, setFamilles] = useState<ImportFamilies | null>(null);
  const [famillesKo, setFamillesKo] = useState<string | null>(null);
  /** Les choix FAITS À LA MAIN — le reste suit l'appris et l'identique. */
  const [choixFamilles, setChoixFamilles] = useState<Record<string, string>>({});
  const [memoriser, setMemoriser] = useState(true);
  /**
   * Le fournisseur pour lequel les familles ont été lues — `null` tant qu'elles ne l'ont pas été.
   * ⚠️ ON N'APPREND QUE POUR LUI : choisi plus tard (étape Destination), un fournisseur n'a vu ni ses correspondances
   *    apprises appliquées ni l'interrupteur « Mémoriser » s'ouvrir — lui en apprendre en silence contredirait l'écran.
   */
  const [famillesPour, setFamillesPour] = useState<string | null>(null);
  const avecFamilles = mapped('fam');
  useEffect(() => {
    if (etape !== 3 || !avecFamilles) return;
    let vivant = true;
    setFamillesKo(null);
    const pour = fournisseur.trim();
    api.importFamilies(pour || null)
      .then((r) => { if (vivant) { setFamilles(r); setFamillesPour(pour); } })
      // ⚠️ L'ÉCHEC SE DIT, ET IL RETIRE CE QUI ÉTAIT LU : une liste vide se lirait « ce magasin n'a aucune famille », et
      //    les familles d'un autre fournisseur gardées feraient apprendre ses correspondances à celui-ci.
      .catch((e: unknown) => { if (vivant) { setFamilles(null); setFamillesPour(null); setFamillesKo(e instanceof Error ? e.message : String(e)); } });
    return () => { vivant = false; };
  }, [etape, avecFamilles, fournisseur]);
  const lignesFamilles = useMemo(() => (familles && analyse
    ? analyse.families.map((raw) => ({ raw, etat: resoudreFamille(raw, familles) }))
    : []), [familles, analyse]);
  const aDefinir = lignesFamilles.filter((l) => choixFamilles[l.raw] === undefined && l.etat.kind !== 'learned' && l.etat.kind !== 'exact').length;

  /** La grille de tailles du fichier — `''` = automatique (celle de l'article, de ses sœurs, ou la seule qui porte la taille). */
  const [grille, setGrille] = useState('');
  const [grilles, setGrilles] = useState<SizeGridOption[] | null>(null);
  const [grillesKo, setGrillesKo] = useState<string | null>(null);
  const avecTailles = mapped('taille') || mapped('long');
  useEffect(() => {
    if (etape !== 3 || !avecTailles) return;
    let vivant = true;
    api.articleSizeGrids()
      .then((r) => { if (vivant) { setGrilles(r.grids); setGrillesKo(null); } })
      .catch((e: unknown) => { if (vivant) setGrillesKo(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [etape, avecTailles]);

  // ── ÉTAPE 4 : DESTINATION ──────────────────────────────────────────────────────────────────────
  const avecQuantites = mapped('qte');
  /** Le choix de l'opérateur — `null` tant qu'il n'a rien touché : le défaut suit la présence d'une colonne Quantité. */
  const [destinationChoisie, setDestination] = useState<ImportDestinationKind | null>(null);
  // ⚠️ SANS COLONNE QUANTITÉ, RIEN NE PEUT PARTIR EN STOCK NI DANS UN DOCUMENT : le défaut devient « Référentiel
  //    seulement », et les trois autres cartes disent pourquoi elles sont fermées.
  const destination: ImportDestinationKind = avecQuantites ? (destinationChoisie ?? 'bl') : 'ref';
  const avecDocument = destination === 'bl' || destination === 'cmd';
  const [modeDocument, setModeDocument] = useState<'new' | 'existing'>('new');
  const [numero, setNumero] = useState('');
  const [proposition, setProposition] = useState<string | null>(null);
  const [documentChoisi, setDocumentChoisi] = useState('');
  const [documents, setDocuments] = useState<ImportOpenDocument[] | null>(null);
  const [documentsKo, setDocumentsKo] = useState<string | null>(null);
  const docKind: 'bl' | 'cmd' = destination === 'cmd' ? 'cmd' : 'bl';
  useEffect(() => {
    if (etape !== 4 || !avecDocument || !fournisseur.trim()) return;
    let vivant = true;
    setDocuments(null);
    setDocumentsKo(null);
    api.importDocuments(docKind, fournisseur.trim())
      .then((r) => { if (vivant) setDocuments(r.documents); })
      .catch((e: unknown) => { if (vivant) setDocumentsKo(e instanceof Error ? e.message : String(e)); });
    api.importDocumentNumber(docKind, fournisseur.trim())
      .then((r) => { if (vivant) setProposition(r.number); })
      // ⚠️ BEST-EFFORT ASSUMÉ : sans proposition, « Proposer » reste inactif et le dit, le numéro se tape, et laissé vide
      //    il est attribué par le serveur — rien n'est perdu ni affirmé à tort.
      .catch(() => { if (vivant) setProposition(null); });
    return () => { vivant = false; };
  }, [etape, avecDocument, docKind, fournisseur]);

  // ⚠️ UN DOCUMENT CHOISI NE VAUT QUE POUR SA DESTINATION ET SON FOURNISSEUR : gardé, un BL partait comme numéro de
  //    commande (refus au lancement), ou les lignes d'un fournisseur entraient dans le bon d'un autre.
  useEffect(() => { setDocumentChoisi(''); }, [docKind, fournisseur]);
  /** Le compteur des ouvertures — une lecture de fichier commencée avant une remise à zéro ne doit pas revenir. */
  const ouverture = useRef(0);

  /** Repart d'une fenêtre vierge — étape 1, aucun fichier, aucun choix. */
  const reset = useCallback(() => {
    setEtape(1); setFichier(null); setMask({}); setFailure(null); setChoixFamilles({}); setFamilles(null); setMemoriser(true);
    setFournisseur(''); setDestination(null); setModeDocument('new'); setNumero(''); setProposition(null); setDocumentChoisi('');
    setDocuments(null); setGrille(''); setDoublons('skip'); setFamillesKo(null); setFamillesPour(null); setGrillesKo(null);
    ouverture.current += 1;
  }, []);
  // ⚠️ CHAQUE OUVERTURE REPART DE ZÉRO (maquette) : rouvrir après « Annuler » sur le fichier d'hier, à l'étape où on
  //    l'avait laissé, ferait lancer un import qu'on croyait abandonné.
  useEffect(() => { if (open) reset(); }, [open, reset]);

  const lancer = useCallback(() => {
    if (!fichier || !analyse || analyse.rows.length === 0) return;
    const families = avecFamilles && familles
      ? Object.fromEntries(lignesFamilles.map((l) => [l.raw, familleRetenue(l.raw, choixFamilles, l.etat)]))
      : undefined;
    const options: ImportArticlesOptions = {
      sizeGrid: grille || null,
      supplier: fournisseur.trim() || null,
      duplicates: doublons,
      ...(families ? { families, learnFamilies: memoriser && fournisseur.trim() !== '' && famillesPour === fournisseur.trim() } : {}),
      destination: {
        kind: destination,
        mode: avecDocument ? modeDocument : 'new',
        number: avecDocument ? (modeDocument === 'existing' ? documentChoisi : numero.trim() || null) : null,
      },
    };
    launchImport({
      // ⚠️ LA CLÉ DE REJEU EST FIXÉE AU LANCEMENT : un envoi rejoué après une coupure rend le même compte rendu.
      key: `import:${fichier.nom}:${Date.now().toString(36)}:${Math.random().toString(36).slice(2, 8)}`,
      fileName: fichier.nom, rows: analyse.rows, options,
      // ⚠️ LES LIGNES ÉCARTÉES PAR L'ANALYSE NE PARTENT PAS, mais le rapport les nomme : c'est lui que l'opérateur garde.
      screenRejected: analyse.bad.map((b) => ({ line: b.line, why: b.why, ex: b.ex })),
    });
    reset();
    onClose();
  }, [fichier, analyse, avecFamilles, familles, famillesPour, lignesFamilles, choixFamilles, grille, fournisseur, doublons, memoriser, destination, avecDocument, modeDocument, documentChoisi, numero, reset, onClose]);

  // ⚠️ ABONNÉ À L'IMPORT EN COURS : lu une fois au rendu, l'état laissait le bouton grisé après la fin de l'import.
  const job = useImportJob();
  const enCours = job?.state === 'running';
  /** Ce qui retient « Continuer » — et pourquoi, pour l'infobulle. */
  const blocage: string | null = fichier === null ? t('Déposez d’abord un fichier.')
    : etape === 2 && champsManquants(masque).length > 0 ? t('Il manque : {v}.', { v: champsManquants(masque).join(', ') })
      // ⚠️ LES FAMILLES DU MAGASIN D'ABORD : parti sans elles, l'import créait une famille par libellé du fichier.
      : etape === 3 && avecFamilles && famillesKo !== null ? t('Les familles du magasin n’ont pas pu être lues : sans elles, chaque famille du fichier serait créée.')
        : etape === 3 && avecFamilles && familles === null ? t('Lecture des familles du magasin…')
      : etape === 4 && avecDocument && !fournisseur.trim() ? t('Choisissez le fournisseur du fichier.')
        : etape === 4 && avecDocument && modeDocument === 'existing' && !documentChoisi ? t('Choisissez le document à compléter.')
          : etape === 5 && (analyse?.rows.length ?? 0) === 0 ? t('Aucune ligne prête à importer.')
            : etape === 5 && (analyse?.rows.length ?? 0) > IMPORT_ROWS_MAX ? t('Plus de {n} lignes : découpez le fichier.', { n: IMPORT_ROWS_MAX })
              : etape === 5 && enCours ? t('Un import est déjà en cours.')
                : null;
  const [survol, setSurvol] = useState(false);

  const take = useCallback(async (f: File | null | undefined) => {
    if (!f) return;
    const pourOuverture = ouverture.current;
    setFailure(null);
    // ⚠️ ON REFUSE PAR L'EXTENSION, AVANT DE LIRE : un binaire passé au lecteur CSV rendrait des lignes de charabia.
    if (!estClasseur(f.name) && !/.csv$/i.test(f.name)) {
      setFailure(t('« {name} » n’est pas lu : déposez un classeur (.xlsx, .xls) ou un fichier .csv.', { name: f.name }));
      return;
    }
    setReadOnly(true);
    try {
      const octets = await f.arrayBuffer();
      const lu = estClasseur(f.name) ? await lireClasseur(octets) : lireCsv(octets);
      // ⚠️ L'ASSISTANT A ÉTÉ FERMÉ PENDANT LA LECTURE : ce fichier abandonné ne doit pas réapparaître à la réouverture.
      if (ouverture.current !== pourOuverture) return;
      if (lu.rows.length === 0) {
        throw new Error(lu.sheet === null
          ? t('Le fichier ne contient aucune ligne exploitable.')
          : t('La feuille « {sheet} » ne contient aucune ligne.', { sheet: lu.sheet }));
      }
      setFichier({ nom: f.name, taille: poids(f.size), octets, lu, hasHeading: ressembleAUnEntete(lu.rows[0]!) });
      setMask(proposerMasque(ressembleAUnEntete(lu.rows[0]!) ? lu.rows[0]! : []));
      setChoixFamilles({});
      // ⚠️ ON AVANCE TOUT DE SUITE : rester sur la zone de dépôt invite à redéposer le même fichier.
      setEtape(2);
    } catch (e) {
      // ⚠️ LE MOTIF RÉEL : « fichier vide » et « lecture impossible » appellent deux gestes différents.
      setFailure(e instanceof Error ? e.message : String(e));
    } finally {
      setReadOnly(false);
    }
  }, []);

  /** ⚠️ RELIT LES OCTETS DÉJÀ EN MÉMOIRE : le navigateur ne garde pas la poignée du fichier déposé. */
  const changerFeuille = useCallback(async (nomFeuille: string) => {
    if (!fichier) return;
    try {
      const lu = await lireClasseur(fichier.octets, nomFeuille);
      const hasHeading = ressembleAUnEntete(lu.rows[0] ?? []);
      setFichier({ ...fichier, lu, hasHeading });
      setMask(proposerMasque(hasHeading ? lu.rows[0] ?? [] : []));
    } catch (e) {
      setFailure(e instanceof Error ? e.message : String(e));
    }
  }, [fichier]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="xxl"
      // ⚠️ HAUTEUR FIGÉE : un parcours à étapes dont le pied se déplace fait cliquer « Continuer » là où était
      //    « Précédent » (900 × 780 à la maquette, absorbés par les crans de la charte).
      capHeight="lg"
      // ⚠️ UN CLIC À CÔTÉ NE FERME PAS : la maquette n'en fait rien, et perdre un masque réglé colonne par colonne
      //    pour trois pixels manqués serait le geste le plus coûteux de l'assistant.
      closeOnBackdrop={false}
      title={t('Importer un fichier')}
      subtitle={t('Excel ou CSV · une ligne = une déclinaison')}
      icon="upload"
      tone="blue"
      // ⚠️ LE FIL NE REVIENT QU'EN ARRIÈRE : sauter en avant afficherait une étape sans sa matière.
      band={<Steps items={ETAPES_IMPORT.map((e) => t(e))} current={etape} onGo={(n) => { if (n < etape) setEtape(n); }} label={t('Étapes de l’import')} tone="blue" bare />}
      footer={
        <>
          {etape > 1 && (
            <button type="button" onClick={() => setEtape((n) => n - 1)} style={{
              height: 40, padding: '0 18px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
              background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-title)', fontWeight: 600, cursor: 'pointer',
            }}>{t('Précédent')}</button>
          )}
          <div style={{ flex: 1 }} />
          <button
            type="button" onClick={onClose}
            style={{
              height: 40, padding: '0 18px', border: 'none', borderRadius: 'var(--r-ctrl)',
              background: 'transparent', color: 'var(--ink-500)', fontSize: 'var(--t-title)', fontWeight: 600, cursor: 'pointer',
            }}
          >{t('Annuler')}</button>
          {/* ⚠️ INACTIF AVEC SON MOTIF EN INFOBULLE : un bouton grisé sans motif se lit « l'application est cassée ». */}
          {etape < 5 && (
            <button
              type="button"
              aria-disabled={blocage !== null || undefined}
              title={blocage ?? undefined}
              onClick={() => { if (blocage === null) setEtape((n) => n + 1); }}
              style={{
                height: 40, padding: '0 20px', border: 'none', borderRadius: 'var(--r-ctrl)', fontSize: 'var(--t-title)', fontWeight: 700,
                background: blocage === null ? 'var(--blue)' : 'var(--ink-200)', color: blocage === null ? '#fff' : 'var(--ink-400)',
                cursor: blocage === null ? 'pointer' : 'not-allowed',
              }}
            >{t('Continuer')}</button>
          )}
          {etape === 5 && (
            <button
              type="button"
              aria-disabled={blocage !== null || undefined}
              title={blocage ?? undefined}
              onClick={() => { if (blocage === null) lancer(); }}
              style={{
                height: 40, padding: '0 20px', border: 'none', borderRadius: 'var(--r-ctrl)', fontSize: 'var(--t-title)', fontWeight: 700,
                background: blocage === null ? 'var(--teal-500)' : 'var(--ink-200)', color: blocage === null ? '#fff' : 'var(--ink-400)',
                cursor: blocage === null ? 'pointer' : 'not-allowed',
              }}
            >{t('Lancer l’import en arrière-plan')}</button>
          )}
        </>
      }
    >
      <div>
        {etape === 1 && (
          <>
            <div style={{ fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)', marginBottom: 3 }}>{t('Déposez le fichier du fournisseur')}</div>
            <p style={{ margin: '0 0 16px', fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>{t('Il est lu dans votre navigateur — rien n’est envoyé avant votre validation.')}</p>
            <label
              onDragOver={(e) => { e.preventDefault(); setSurvol(true); }}
              onDragLeave={() => setSurvol(false)}
              onMouseEnter={() => setSurvol(true)}
              onMouseLeave={() => setSurvol(false)}
              onDrop={(e) => { e.preventDefault(); setSurvol(false); void take(e.dataTransfer.files[0]); }}
              style={{
                display: 'block', padding: '38px 24px', borderRadius: 'var(--r-md)', textAlign: 'center',
                border: `2px dashed ${survol ? 'var(--blue)' : 'var(--ink-300)'}`,
                background: survol ? 'var(--blue-bg)' : 'var(--ink-50)',
                cursor: 'pointer', transition: 'background .12s ease, border-color .12s ease',
              }}
            >
              <span style={{
                display: 'flex', width: 48, height: 48, margin: '0 auto 14px', borderRadius: 'var(--r-md)',
                background: 'var(--paper)', border: '1px solid var(--ink-200)', color: 'var(--blue-fg)',
                alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon name="upload" size={23} stroke={1.8} />
              </span>
              <span style={{ display: 'block', fontSize: 'var(--t-control)', fontWeight: 600, color: 'var(--ink-900)' }}>{t('Glissez votre fichier ici')}</span>
              <span style={{ display: 'block', fontSize: 'var(--t-body)', color: 'var(--ink-500)', marginTop: 4 }}>{t('ou cliquez pour le choisir · .xlsx, .xls, .csv')}</span>
              <input type="file" accept={EXTENSIONS_LUES} style={{ display: 'none' }} onChange={(e) => { void take(e.target.files?.[0]); e.target.value = ''; }} />
            </label>
            {lecture && <div style={{ marginTop: 12, fontSize: 'var(--t-lead)', color: 'var(--ink-600)' }}>{t('Lecture du fichier…')}</div>}
            {failure !== null && (
              <div role="alert" style={{
                marginTop: 12, padding: '12px 14px', borderRadius: 'var(--r-sm)', background: 'var(--rose-bg)',
                border: '1px solid var(--rose)', color: 'var(--rose-fg)', fontSize: 'var(--t-body)',
              }}>{failure}</div>
            )}
          </>
        )}

        {etape === 2 && fichier !== null && (
          <EtapeColonnes
            fichier={fichier} masque={masque} onMask={setMask}
            // ⚠️ LA CORRESPONDANCE RESTE (maquette) : basculer l'en-tête change les lignes lues, pas ce qu'est chaque
            //    colonne — la refaire effacerait le travail fait colonne par colonne.
            onHeading={(v) => setFichier({ ...fichier, hasHeading: v })}
            onFeuille={(f) => void changerFeuille(f)}
            masks={masks} onMasksChanged={reloadMasks}
            fournisseur={fournisseur} onFournisseur={setFournisseur}
            fournisseurs={fournisseurs} fournisseursKo={fournisseursKo}
          />
        )}

        {etape === 3 && (
          <EtapeDoublons
            doublons={doublons} onDoublons={setDoublons}
            avecFamilles={avecFamilles} lignes={lignesFamilles} familles={familles} famillesKo={famillesKo}
            choix={choixFamilles} onChoix={(raw, v) => setChoixFamilles((c) => ({ ...c, [raw]: v }))} aDefinir={aDefinir}
            memoriser={memoriser} onMemoriser={setMemoriser} fournisseur={fournisseur}
            avecTailles={avecTailles} grille={grille} onGrille={setGrille} grilles={grilles} grillesKo={grillesKo}
          />
        )}

        {etape === 4 && (
          <EtapeDestination
            destination={destination} onDestination={setDestination} avecQuantites={avecQuantites}
            mode={modeDocument} onMode={(m) => { setModeDocument(m); setDocumentChoisi(''); }}
            numero={numero} onNumero={setNumero} proposition={proposition}
            documents={documents} documentsKo={documentsKo} documentChoisi={documentChoisi} onDocument={setDocumentChoisi}
            fournisseur={fournisseur} onFournisseur={setFournisseur} fournisseurs={fournisseurs} fournisseursKo={fournisseursKo}
            avecQuantites5={doublons === 'skip' && avecQuantites}
          />
        )}

        {etape === 5 && analyse !== null && (
          <EtapeRapport analyse={analyse} destination={destination} doublons={doublons} numero={avecDocument ? (modeDocument === 'existing' ? documentChoisi : numero.trim()) : ''} />
        )}
      </div>
    </Modal>
  );
}

/** Les deux lignes d'une tuile de compte : le nombre, puis sa légende. */
type TileLines = readonly [(x: string) => ReactNode, (x: ReactNode) => ReactNode];
const TILE_LINES = (ink: string): TileLines => [
  (x) => <div style={{ fontSize: '26px', fontWeight: 800, letterSpacing: '-0.03em', color: ink, fontVariantNumeric: 'tabular-nums', marginTop: 5 }}>{x}</div>,
  (x: ReactNode) => <div style={{ fontSize: 'var(--t-note)', color: ink === 'var(--teal-800)' ? 'var(--teal-800)' : 'var(--ink-500)', marginTop: 2, opacity: ink === 'var(--teal-800)' ? 0.8 : 1 }}>{x}</div>,
];

/**
 * Une tuile de compte — rubrique mono, nombre en 26 px, légende.
 * ⚠️ LA TUILE PRÊTE SES DEUX LIGNES : le nombre et sa légende sont une seule phrase comptée (`<Trans>`), que le
 *    catalogue accorde. Reçus séparément, ils affichaient « 1 écartées » quelle que soit la langue.
 */
export function Tuile({ titre, tone, count }: { titre: string; tone?: 'teal'; count: (lines: TileLines) => ReactNode }) {
  const teal = tone === 'teal';
  return (
    <div style={{ padding: 16, border: `1px solid ${teal ? 'var(--teal-300)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-sm)', background: teal ? 'var(--teal-100)' : 'var(--paper)' }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: teal ? 'var(--teal-800)' : 'var(--ink-500)' }}>{titre}</div>
      {count(TILE_LINES(teal ? 'var(--teal-800)' : 'var(--ink-900)'))}
    </div>
  );
}

/** Le titre et le chapeau d'une étape — 15 px gras, puis la phrase qui dit ce qu'on décide ici. */
function Chapeau({ titre, note }: { titre: string; note: string }) {
  return (
    <>
      <div style={{ fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)', marginBottom: 3 }}>{titre}</div>
      <p style={{ margin: '0 0 16px', fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>{note}</p>
    </>
  );
}

/** Un encart gris de la maquette — ce que l'étape ne dit pas autrement. */
function Note({ children }: { children: ReactNode }) {
  return (
    <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', fontSize: 'var(--t-body)', color: 'var(--ink-600)', lineHeight: 1.55 }}>
      {children}
    </div>
  );
}

/** Les deux raccourcis de l'étape Colonnes — même gabarit, 32 px au relevé. */
function PetitBouton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} style={{
      height: 32, padding: '0 12px', borderRadius: 'var(--r-ctrl)', fontSize: 'var(--t-body)', fontWeight: 600, whiteSpace: 'nowrap',
      border: '1px solid var(--ink-200)', background: 'var(--paper)', color: 'var(--ink-700)', cursor: 'pointer',
    }}>{children}</button>
  );
}

/**
 * ÉTAPE 2 — CE QUE LE FICHIER CONTIENT, et à quel champ va chaque colonne, choisi DANS L'EN-TÊTE du tableau (maquette).
 *
 * ⚠️ L'EN-TÊTE EST PROPOSÉ, JAMAIS IMPOSÉ : la détection se trompe sur un fichier dont la première ligne est déjà une
 *    donnée, et l'article perdu serait le PREMIER — la perte la plus difficile à voir.
 * ⚠️ LE CONTRÔLE DU SOCLE, PAS UN `<select>` NATIF (défaut signalé à l'usage) : le menu natif ignore la charte et ne se
 *    cherche pas au clavier sur une liste de quinze champs.
 * ⚠️ RAPPELER UN MASQUE APPLIQUE PAR POSITION, et une colonne absente retombe sur « ne pas importer » : un fournisseur
 *    qui retire une colonne décalerait sinon tout ce qui suit.
 */
function EtapeColonnes({ fichier, masque, onMask, onHeading, onFeuille, masks, onMasksChanged, fournisseur, onFournisseur, fournisseurs, fournisseursKo }: {
  fichier: FichierCharge;
  masque: Masque;
  onMask: (m: Masque) => void;
  onHeading: (v: boolean) => void;
  onFeuille: (nom: string) => void;
  masks: readonly ImportMask[];
  onMasksChanged: () => void;
  fournisseur: string;
  onFournisseur: (v: string) => void;
  fournisseurs: string[] | null;
  fournisseursKo: string | null;
}) {
  const { lu, hasHeading, nom, taille } = fichier;
  const colonnes = lu.rows[0]?.length ?? 0;
  const rows = hasHeading ? Math.max(0, lu.rows.length - 1) : lu.rows.length;
  const headingRow = hasHeading ? lu.rows[0] ?? [] : null;
  const debut = headingRow ? 1 : 0;
  const corps = lu.rows.slice(debut, debut + 6);
  const retenues = colonnesRetenues(masque).length;
  const manquants = champsManquants(masque);
  const canWrite = useAuth().can('bo.write');
  const [nomMasque, setNomMasque] = useState('');
  const [writing, setWriting] = useState(false);
  const [supprime, setSupprime] = useState<string | null>(null);
  const propose = nom.replace(/\.[^.]+$/, '').slice(0, 60);

  const save = async () => {
    const n = (nomMasque.trim() || propose).trim();
    if (n === '' || writing) return;
    setWriting(true);
    try {
      await api.saveImportMask(n, fournisseur.trim(), masque);
      showToast(t('Masque « {nom} » enregistré.', { nom: n }));
      onMasksChanged();
      setNomMasque('');
    } catch (e) {
      // ⚠️ LE MOTIF RÉEL : « aucune colonne rattachée » et « liste pleine » appellent deux gestes.
      showToast(e instanceof Error ? e.message : String(e), 'err');
    } finally {
      setWriting(false);
    }
  };

  /** OUBLIER UN MASQUE — la croix de la pastille, offerte à qui peut écrire (la route l'exige de toute façon). */
  const oublier = async (maskName: string) => {
    if (supprime !== null) return;
    const accord = await confirm({
      title: t('Oublier le masque « {maskName} » ?', { maskName }),
      message: t('La correspondance des colonnes mémorisée sera perdue. Les articles déjà importés avec ce masque ne sont pas touchés.'),
      confirmLabel: t('Oublier'),
      tone: 'danger',
    });
    if (!accord) return;
    setSupprime(maskName);
    try {
      await api.deleteImportMask(maskName);
      showToast(t('Masque « {maskName} » oublié.', { maskName }));
      onMasksChanged();
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
    } finally {
      setSupprime(null);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)', marginBottom: 3 }}>{t('Voici ce que contient votre fichier')}</div>
          <p style={{ margin: 0, fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>
            <Trans
              k="<1>{nom}</1> · <2>{n}</2> ligne, <3>{colonnes}</3> colonnes · {taille}"
              vars={{ nom, n: rows, colonnes, taille }}
              parts={[
                (x) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-dense)', color: 'var(--ink-700)' }}>{x}</span>,
                (x) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{x}</span>,
                (x) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{x}</span>,
              ]}
            />
            {' — '}{t('affectez chaque colonne à son champ Sliderz directement dans l’en-tête du tableau.')}
          </p>
        </div>
        {/* ⚠️ UNE BASCULE, PAS UNE CASE À COCHER NATIVE : c'est le geste le plus lourd de conséquence de l'étape. */}
        <button
          type="button" role="switch" aria-checked={hasHeading} onClick={() => onHeading(!hasHeading)}
          className={OZ_INTERACTIVE_SURFACE}
          style={{
            height: 32, padding: '0 12px', borderRadius: 'var(--r-ctrl)', fontSize: 'var(--t-body)', fontWeight: 600, whiteSpace: 'nowrap',
            border: '1px solid var(--ink-200)', background: 'var(--paper)', color: 'var(--ink-700)', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0,
          }}
        >
          <span style={{
            width: 16, height: 16, borderRadius: 'var(--r-xs)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: hasHeading ? 'var(--teal-500)' : 'transparent', border: hasHeading ? 'none' : '1.5px solid var(--ink-300)', color: '#fff',
          }}>
            {hasHeading && <Icon name="check" size={11} stroke={3} />}
          </span>
          {t('La première ligne est un en-tête')}
        </button>
      </div>

      {/* ⚠️ LE CHOIX DE FEUILLE N'APPARAÎT QUE S'IL Y EN A PLUSIEURS. */}
      {lu.sheets.length > 1 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {lu.sheets.map((f) => {
            const active = f === lu.sheet;
            return (
              <button key={f} type="button" onClick={() => onFeuille(f)} aria-pressed={active} style={{
                height: 32, padding: '0 12px', borderRadius: 'var(--r-ctrl)', fontSize: 'var(--t-body)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer',
                border: `1px solid ${active ? 'var(--blue)' : 'var(--ink-200)'}`, background: active ? 'var(--blue-bg)' : 'var(--paper)',
                color: active ? 'var(--blue-fg)' : 'var(--ink-700)',
              }}>{f}</button>
            );
          })}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 12 }}>
        {masks.length > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 'var(--t-note)', fontWeight: 500, color: 'var(--ink-500)' }}>{t('Rappeler un masque :')}</span>
            {/* ⚠️ LA CROIX EST DANS LA PASTILLE : c'est là qu'on lit le nom, donc là qu'on décide de ne plus l'avoir. */}
            {masks.map((m) => (
              <span key={m.name} className="bo-import-mask" style={{
                display: 'inline-flex', alignItems: 'center', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-pill)',
                background: 'var(--paper)', opacity: supprime === m.name ? 0.5 : 1,
              }}>
                {/* ⚠️ LE NOM SEUL, comme la maquette : le fournisseur du masque se lit dans le bloc du masque, où il se pose. */}
                <button
                  type="button"
                  title={m.supplier ? t('Masque de {supplier}', { supplier: m.supplier }) : undefined}
                  onClick={() => {
                    const memoire: Masque = {};
                    for (const [i, k] of Object.entries(m.mapping)) memoire[Number(i)] = k;
                    onMask(appliquerMasque(memoire, colonnes));
                    if (m.supplier) onFournisseur(m.supplier);
                  }}
                  style={{
                    height: 32, padding: canWrite ? '0 6px 0 12px' : '0 12px', border: 'none', borderRadius: 'var(--r-pill)',
                    background: 'transparent', color: 'var(--ink-800)', fontSize: 'var(--t-dense)', fontWeight: 600, cursor: 'pointer',
                    whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}
                >
                  <Icon name="lines" size={12} stroke={1.9} style={{ flexShrink: 0 }} />
                  {m.name}
                </button>
                {canWrite && (
                  <button
                    type="button" onClick={() => void oublier(m.name)}
                    aria-label={t('Oublier le masque {name}', { name: m.name })}
                    title={t('Oublier le masque « {name} »', { name: m.name })}
                    style={{
                      height: 32, width: 26, border: 'none', background: 'transparent', borderRadius: 'var(--r-pill)', color: 'var(--ink-400)',
                      cursor: supprime === null ? 'pointer' : 'progress', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <Icon name="x" size={12} stroke={2.4} />
                  </button>
                )}
              </span>
            ))}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <PetitBouton onClick={() => onMask(proposerMasque(headingRow ?? []))}>{t('Proposer à nouveau')}</PetitBouton>
        <PetitBouton onClick={() => onMask(Object.fromEntries(Array.from({ length: colonnes }, (_, i) => [i, 'skip'])))}>{t('Tout ignorer')}</PetitBouton>
        <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-600)', whiteSpace: 'nowrap' }}>
          <Trans k='<1>{n}</1> colonne retenue' vars={{ n: retenues }} parts={[(x) => <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{x}</strong>]} />
        </span>
      </div>

      <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflow: 'auto', maxHeight: 380 }}>
        <div style={{ display: 'inline-block', minWidth: '100%' }}>
          {/* ⚠️ COLLÉ EN HAUT : le tableau défile, et une colonne sans son champ ni son intitulé ne se lit plus. */}
          <div style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)' }}>
            <div style={{ display: 'flex' }}>
              <div style={{
                width: 44, flexShrink: 0, padding: '0 8px', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em',
                textTransform: 'uppercase', color: 'var(--ink-500)', borderRight: '1px solid var(--ink-200)', display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
              }}>L</div>
              {Array.from({ length: colonnes }, (_, i) => {
                const k = masque[i] ?? 'skip';
                return (
                  <div key={`m${i}`} style={{ width: 148, flexShrink: 0, padding: '7px 8px', borderRight: '1px solid var(--ink-100)', boxSizing: 'border-box' }}>
                    {/* ⚠️ LE CONTRÔLE REMPLIT SA CELLULE (`cell-head`) : au plancher de 220 px d'un champ, il débordait sous la
                        colonne voisine et son texte se lisait coupé. La PRÉCISION d'un champ (« obligatoire ») vit dans le
                        panneau, jamais dans le bouton — 132 px ne portent qu'un mot. « Ne pas importer » est la première
                        rangée : remettre une colonne à l'écart doit rester un geste, pas un « Tout ignorer ». */}
                    <Picker
                      id={`bo-import-col-${i}`}
                      variant="cell-head"
                      label={t('Champ alimenté par la colonne {v}', { v: headingRow?.[i] || i + 1 })}
                      allLabel={t('Ne pas importer')}
                      clearLabel={t('Ne pas importer')}
                      panelWidth={260}
                      options={CHAMPS_IMPORT.filter((c) => c.k !== 'skip').map((c) => ({
                        value: c.k, label: champLabel(c), ...(c.hint ? { hint: t(c.hint) } : {}),
                      }))}
                      value={k === 'skip' ? '' : k}
                      onChange={(v) => onMask({ ...masque, [i]: v || 'skip' })}
                    />
                  </div>
                );
              })}
            </div>
            {headingRow !== null && (
              <div style={{ display: 'flex', borderTop: '1px solid var(--ink-100)' }}>
                <div style={{ width: 44, flexShrink: 0, borderRight: '1px solid var(--ink-200)' }} />
                {headingRow.map((c, j) => (
                  <div key={`h${j}`} title={c} style={{
                    width: 148, flexShrink: 0, padding: '7px 11px', fontSize: 'var(--t-note)', fontWeight: 700, boxSizing: 'border-box',
                    color: (masque[j] ?? 'skip') === 'skip' ? 'var(--ink-400)' : 'var(--ink-800)', borderRight: '1px solid var(--ink-100)',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{c || t('Colonne {v}', { v: j + 1 })}</div>
                ))}
              </div>
            )}
          </div>
          {corps.map((r, i) => (
            <div key={`r${i}`} style={{ display: 'flex', borderBottom: '1px solid var(--ink-100)' }}>
              <div style={{
                width: 44, flexShrink: 0, padding: 8, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-400)',
                textAlign: 'right', borderRight: '1px solid var(--ink-200)', fontVariantNumeric: 'tabular-nums',
              }}>{lu.lines[debut + i] ?? debut + i + 1}</div>
              {r.map((c, j) => (
                <div key={`c${j}`} title={c} style={{
                  width: 148, flexShrink: 0, padding: '8px 11px', fontSize: 'var(--t-body)', boxSizing: 'border-box',
                  color: (masque[j] ?? 'skip') === 'skip' ? 'var(--ink-300)' : 'var(--ink-700)', borderRight: '1px solid var(--ink-100)',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{c}</div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ⚠️ « LE SÉPARATEUR » SEULEMENT POUR UN CSV : un classeur n'en a pas, l'annoncer serait dire faux. */}
      <Note>
        {lu.sheet !== null
          ? t('Six premières lignes seulement — l’import traitera tout le fichier.')
          : t('Six premières lignes seulement — l’import traitera tout le fichier. Le séparateur a été détecté automatiquement.')}
      </Note>

      {/* ⚠️ LES DEUX COLONNES INDISPENSABLES SONT NOMMÉES (maquette), sous le mot du magasin. */}
      {manquants.length > 0 && (
        <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 'var(--r-sm)', background: 'var(--amber-bg)', border: '1px solid var(--amber)', fontSize: 'var(--t-body)', color: 'var(--ink-800)' }}>
          <Trans
            k='Il manque au minimum une colonne <1>{designation}</1> et une colonne <2>{prix}</2>.'
            vars={{ designation: fieldLabel('name'), prix: fieldLabel('salePriceInclTax') }}
            parts={[(x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>]}
          />
        </div>
      )}

      <div style={{ marginTop: 14, padding: '14px 16px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)' }}>
        <span id="bo-import-mask-title" style={{ display: 'block', fontSize: 'var(--t-note)', fontWeight: 500, color: 'var(--ink-500)', marginBottom: 5 }}>
          {t('Enregistrer ce masque pour les prochains imports de ce fournisseur')}
        </span>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.4fr) minmax(0,1fr) auto', gap: 9 }}>
          <input
            aria-label={t('Nom du masque')}
            value={nomMasque} onChange={(e) => setNomMasque(e.target.value)}
            placeholder={propose || t('Nom du masque')}
            onKeyDown={(e) => { if (e.key === 'Enter') void save(); }}
            style={{
              width: '100%', height: 40, padding: '0 12px', boxSizing: 'border-box', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
              background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-title)', outline: 'none',
            }}
          />
          {/* ⚠️ C'EST LE FOURNISSEUR DU FICHIER, pas seulement celui du masque : il apprend les familles et reçoit le bon.
              ⚠️ « Choisir un fournisseur… » EN TÊTE de la liste (maquette) : le choix se défait comme il se fait. */}
          <Picker
            id="bo-import-fournisseur"
            variant="field-bare"
            label={fieldLabel('supplier')}
            allLabel={t('Choisir un fournisseur…')}
            clearLabel={t('Choisir un fournisseur…')}
            options={(fournisseurs ?? []).map((s) => ({ value: s, label: s }))}
            loading={fournisseurs === null && fournisseursKo === null}
            unavailable={fournisseursKo !== null}
            unavailableReason={fournisseursKo ?? undefined}
            value={fournisseur}
            onChange={onFournisseur}
          />
          <button
            type="button" onClick={() => void save()} aria-disabled={writing || undefined}
            style={{
              height: 40, padding: '0 15px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-900)', color: 'var(--paper)',
              fontSize: 'var(--t-lead)', fontWeight: 600, whiteSpace: 'nowrap', cursor: writing ? 'progress' : 'pointer',
            }}
          >{writing ? t('Enregistrement…') : t('Enregistrer')}</button>
        </div>
        <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 9, lineHeight: 1.5 }}>
          {t('Le masque mémorise seulement la correspondance des colonnes — aucune transformation de valeur.')}
        </div>
      </div>
    </div>
  );
}

/**
 * LES DEUX GABARITS DE CARTE DE LA MAQUETTE — « Doublons » plus ample (deux cartes côte à côte, une case vide qui dit
 * que l'autre choix existe), « Destination » plus serré (quatre cartes, la coche seule sur la carte choisie).
 */
const CARTE = {
  doublon: { gap: 11, pad: 18, head: 10, tile: 38, tileRadius: 'var(--r-sm)', icon: 19, check: 22, checkIcon: 13, title: 'var(--t-control)', note: 'var(--t-body)', noteLine: 1.55, emptyCheck: true },
  destination: { gap: 10, pad: 16, head: 9, tile: 36, tileRadius: 'var(--r-ctrl)', icon: 18, check: 20, checkIcon: 12, title: 'var(--t-title)', note: 'var(--t-dense)', noteLine: 1.5, emptyCheck: false },
} as const;

/** Une carte de choix exclusif de la maquette — Doublons et Destination. */
function CarteChoix({ look, on, icon, title, badge, note, roving, onPick }: {
  look: keyof typeof CARTE; on: boolean; icon: IconName; title: string; badge?: string; note: string;
  roving: ReturnType<typeof rovingRadio>; onPick: () => void;
}) {
  const g = CARTE[look];
  return (
    <button
      type="button" role="radio" aria-checked={on}
      tabIndex={roving.tabIndex} onKeyDown={roving.onKeyDown}
      onClick={onPick}
      style={{
        display: 'flex', flexDirection: 'column', gap: g.gap, padding: g.pad, height: '100%', textAlign: 'left', cursor: 'pointer',
        border: `1.5px solid ${on ? 'var(--blue)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-md)', background: on ? 'var(--blue-bg)' : 'var(--paper)',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: g.head, width: '100%' }}>
        <span style={{
          width: g.tile, height: g.tile, borderRadius: g.tileRadius, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: on ? 'var(--blue)' : 'var(--ink-100)', color: on ? '#fff' : 'var(--ink-500)',
        }}>
          <Icon name={icon} size={g.icon} stroke={1.8} />
        </span>
        <span style={{ flex: 1 }} />
        {on
          ? <span style={{ width: g.check, height: g.check, borderRadius: 'var(--r-pill)', background: 'var(--blue)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon name="check" size={g.checkIcon} stroke={3} />
          </span>
          : g.emptyCheck && <span style={{ width: g.check, height: g.check, borderRadius: 'var(--r-pill)', border: '1.5px solid var(--ink-300)', flexShrink: 0 }} />}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: g.title, fontWeight: 700, color: 'var(--ink-900)', lineHeight: 1.3 }}>{title}</span>
        {badge && (
          <span style={{
            height: 19, padding: '0 8px', borderRadius: 'var(--r-pill)', background: 'var(--teal-100)', color: 'var(--teal-800)', fontSize: '10.5px',
            fontWeight: 700, display: 'inline-flex', alignItems: 'center', letterSpacing: '.03em', textTransform: 'uppercase',
          }}>{badge}</span>
        )}
      </span>
      <span style={{ fontSize: g.note, color: 'var(--ink-600)', lineHeight: g.noteLine }}>{note}</span>
    </button>
  );
}

/**
 * ÉTAPE 3 — DOUBLONS : ce que devient un article déjà en base, vers quelle famille du magasin va chaque famille du
 * fichier, et dans quelle grille se rangent les tailles que rien d'autre ne place.
 * ⚠️ « IGNORER » NE RETIENT QUE LA FICHE : la quantité annoncée par le fichier part quand même à sa destination — un
 *    fichier de livraison d'un fournisseur régulier porte surtout des articles déjà connus.
 */
function EtapeDoublons({
  doublons, onDoublons, avecFamilles, lignes, familles, famillesKo, choix, onChoix, aDefinir, memoriser, onMemoriser, fournisseur,
  avecTailles, grille, onGrille, grilles, grillesKo,
}: {
  doublons: 'skip' | 'update'; onDoublons: (v: 'skip' | 'update') => void;
  avecFamilles: boolean; lignes: { raw: string; etat: FamilleEtat }[]; familles: ImportFamilies | null; famillesKo: string | null;
  choix: Record<string, string>; onChoix: (raw: string, v: string) => void; aDefinir: number;
  memoriser: boolean; onMemoriser: (v: boolean) => void; fournisseur: string;
  avecTailles: boolean; grille: string; onGrille: (v: string) => void; grilles: SizeGridOption[] | null; grillesKo: string | null;
}) {
  const options = [
    { k: 'skip' as const, title: t('Ignorer la ligne'), badge: t('Recommandé'), icon: 'shield-check' as IconName, note: t('L’article existant n’est pas touché. Vos prix, vos familles et vos photos sont préservés ; la ligne apparaît au rapport.') },
    { k: 'update' as const, title: t('Écraser avec le fichier'), icon: 'swap-h' as IconName, note: t('Les champs présents dans le fichier remplacent l’existant. Chaque modification est tracée dans l’historique de l’article.') },
  ];
  const libelleDe = (code: string) => familles?.families.find((f) => f.code === code)?.label ?? code;
  return (
    <div>
      <Chapeau titre={t('Et si un article existe déjà ?')} note={t('La comparaison se fait sur le code-barres — ou, sans code-barres, sur le code article, la couleur et la taille.')} />
      <div role="radiogroup" aria-label={t('Et si un article existe déjà ?')} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(268px, 1fr))', gap: 12, alignItems: 'stretch' }}>
        {options.map((o, i) => (
          <CarteChoix
            key={o.k} look="doublon" on={doublons === o.k} icon={o.icon} title={o.title} badge={o.badge} note={o.note}
            roving={rovingRadio({ index: i, count: options.length, checked: doublons === o.k, anyChecked: true, onPick: (n) => onDoublons(options[n]!.k) })}
            onPick={() => onDoublons(o.k)}
          />
        ))}
      </div>

      {avecFamilles && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Familles du fichier')}</div>
          {famillesKo !== null ? (
            <p role="alert" style={{ margin: '6px 0 0', fontSize: 'var(--t-body)', color: 'var(--danger-ink)' }}>{t('Les familles du magasin n’ont pas pu être lues : {motif}', { motif: famillesKo })}</p>
          ) : familles === null ? (
            <p style={{ margin: '6px 0 0', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Lecture des familles du magasin…')}</p>
          ) : (
            <>
              <p style={{ margin: '3px 0 12px', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>
                <Trans
                  k='Chaque famille du fournisseur pointe vers une famille de votre référentiel. <1>{n} à définir</1> sur {total}.'
                  vars={{ n: aDefinir, total: lignes.length }}
                  parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]}
                />
              </p>
              <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 96px minmax(0,1fr)', gap: 10, padding: '9px 14px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)' }}>
                  {[t('Dans le fichier'), t('État'), t('Votre famille')].map((h) => (
                    <div key={h} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>{h}</div>
                  ))}
                </div>
                <div style={{ maxHeight: 210, overflowY: 'auto', overflowX: 'hidden' }}>
                  {lignes.map((l) => {
                    const auto = l.etat.kind === 'learned' || l.etat.kind === 'exact';
                    const badge = l.etat.kind === 'learned' ? t('Appris') : l.etat.kind === 'exact' ? t('Identique')
                      : l.etat.kind === 'near' ? t('Proche · {pct}', { pct: l.etat.pct }) : t('À définir');
                    const retenue = familleRetenue(l.raw, choix, l.etat);
                    return (
                      <div key={l.raw} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 96px minmax(0,1fr)', gap: 10, padding: '8px 14px', borderBottom: '1px solid var(--ink-100)', alignItems: 'center' }}>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink-800)', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.raw}</div>
                        <div title={l.etat.kind === 'near' ? t('Ressemble à « {v} »', { v: libelleDe(l.etat.target) }) : undefined}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', height: 21, padding: '0 8px', borderRadius: 'var(--r-pill)', fontSize: '10.5px', fontWeight: 700, whiteSpace: 'nowrap',
                            background: auto ? 'var(--teal-100)' : l.etat.kind === 'near' ? 'var(--amber-bg)' : 'var(--ink-100)',
                            color: auto ? 'var(--teal-800)' : l.etat.kind === 'near' ? 'var(--amber-fg)' : 'var(--ink-600)',
                          }}>{badge}</span>
                        </div>
                        {/* ⚠️ « Ne pas classer » EN TÊTE (maquette) : un choix fait se défait par le même geste. */}
                        <Picker
                          id={`bo-import-fam-${normalizeCode(l.raw)}`}
                          variant="cell"
                          label={t('Famille du magasin pour « {v} »', { v: l.raw })}
                          allLabel={t('Ne pas classer')}
                          clearLabel={t('Ne pas classer')}
                          panelWidth={260}
                          options={[
                            ...familles.families.map((f) => ({ value: f.code, label: f.label })),
                            { value: CREATE_FAMILY, label: t('Créer « {v} »', { v: l.raw }) },
                          ]}
                          value={retenue}
                          onChange={(v) => onChoix(l.raw, v)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* ⚠️ SANS FOURNISSEUR, RIEN NE SE MÉMORISE — une correspondance vaut pour UN fournisseur, et l'écran le dit. */}
              <button
                type="button" role="switch" aria-checked={memoriser && fournisseur.trim() !== ''}
                aria-disabled={fournisseur.trim() === '' || undefined}
                onClick={() => { if (fournisseur.trim()) onMemoriser(!memoriser); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 11, width: '100%', marginTop: 12, padding: '12px 14px', textAlign: 'left',
                  border: '1px solid var(--teal-300)', borderRadius: 'var(--r-sm)', background: 'var(--teal-100)', cursor: fournisseur.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                <span style={{
                  width: 38, height: 22, borderRadius: 'var(--r-pill)', flexShrink: 0, display: 'flex', alignItems: 'center', padding: '0 3px',
                  justifyContent: memoriser && fournisseur.trim() ? 'flex-end' : 'flex-start',
                  background: memoriser && fournisseur.trim() ? 'var(--teal-500)' : 'var(--ink-200)',
                }}><span style={{ width: 16, height: 16, borderRadius: 'var(--r-pill)', background: '#fff' }} /></span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--teal-800)' }}>{t('Mémoriser ces correspondances')}</span>
                  <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-600)', lineHeight: 1.45, marginTop: 2 }}>
                    {fournisseur.trim()
                      ? t('Les prochains fichiers de ce fournisseur seront classés automatiquement.')
                      : t('Choisissez le fournisseur du fichier (étape Colonnes) pour mémoriser : une correspondance vaut pour un fournisseur.')}
                  </span>
                </span>
              </button>
            </>
          )}
        </div>
      )}

      {avecTailles && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)', marginBottom: 6 }}>{t('Grille de tailles du fichier')}</div>
          {grillesKo
            ? <span role="alert" style={{ fontSize: 'var(--t-note)', color: 'var(--danger-ink)' }}>{t('Les grilles n’ont pas pu être lues : {grillesKo}', { grillesKo })}</span>
            : (
              <Picker
                id="import-grille" label={t('Grille de tailles du fichier')} value={grille} onChange={(v) => onGrille(v)}
                allLabel={t('Automatique — la grille de l’article ou de ses sœurs, sinon la seule qui porte la taille')}
                options={(grilles ?? []).map((g) => ({ value: g.name, label: g.name }))}
                loading={grilles === null}
                variant="field-inline"
              />
            )}
          <p style={{ margin: '6px 0 0', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
            <Trans k='Une taille que la grille ne connaît pas y est <1>ajoutée</1> ; une longueur (dimension 2) va dans la grille rattachée, créée s’il le faut. Une taille que rien ne place est refusée, ligne par ligne, dans le rapport.' parts={[(x) => <strong>{x}</strong>]} />
          </p>
        </div>
      )}

      <Note>{t('Une ligne sans code-barres est rapprochée par son code article, sa couleur et sa taille ; sinon elle crée un article, avec un code-barres interne du magasin et, s’il manque, un code article tiré de la désignation.')}</Note>
    </div>
  );
}

/**
 * ÉTAPE 4 — DESTINATION : où vont les quantités du fichier.
 * ⚠️ « BON DE LIVRAISON » EST LE DÉFAUT (maquette) : un fichier fournisseur annonce une livraison, il ne la constate pas.
 * ⚠️ SANS COLONNE QUANTITÉ, SEULE « ARTICLES SEULEMENT » EST PROPOSÉE (décision utilisateur) : un bon, une commande ou
 *    une entrée en stock sans quantité n'ont pas de sens — les montrer, même fermés, proposerait ce qui ne se peut pas.
 */
function EtapeDestination({
  destination, onDestination, avecQuantites, mode, onMode, numero, onNumero, proposition, documents, documentsKo, documentChoisi, onDocument,
  fournisseur, onFournisseur, fournisseurs, fournisseursKo, avecQuantites5,
}: {
  destination: ImportDestinationKind; onDestination: (v: ImportDestinationKind) => void; avecQuantites: boolean;
  mode: 'new' | 'existing'; onMode: (v: 'new' | 'existing') => void;
  numero: string; onNumero: (v: string) => void; proposition: string | null;
  documents: ImportOpenDocument[] | null; documentsKo: string | null; documentChoisi: string; onDocument: (v: string) => void;
  fournisseur: string; onFournisseur: (v: string) => void; fournisseurs: string[] | null; fournisseursKo: string | null;
  /** « Ignorer » est retenu et le fichier porte des quantités : celles des articles déjà en base partent quand même. */
  avecQuantites5: boolean;
}) {
  const choisi = DESTINATIONS.find((d) => d.k === destination)!;
  const avecDocument = destination === 'bl' || destination === 'cmd';
  const offertes = avecQuantites ? DESTINATIONS : DESTINATIONS.filter((d) => d.k === 'ref');
  const modes: { k: 'new' | 'existing'; l: string }[] = [
    { k: 'new', l: destination === 'cmd' ? t('Nouvelle commande') : t('Nouveau bon de livraison') },
    { k: 'existing', l: t('Rattacher à un document existant') },
  ];
  return (
    <div>
      <Chapeau titre={t('Où vont ces quantités ?')} note={t('Comme pour une création manuelle — le référentiel et le stock sont deux gestes distincts.')} />
      {/* ⚠️ `auto-fill` ET NON `auto-fit` (la maquette, qui montre toujours ses quatre cartes) : `auto-fit` replie les
          colonnes vides, et « Articles seulement », seule quand le fichier n'a pas de quantité, s'étirait sur toute la
          largeur. `auto-fill` les garde : la carte seule a la largeur d'une des quatre. */}
      <div role="radiogroup" aria-label={t('Où vont ces quantités ?')} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(194px, 1fr))', gap: 11, alignItems: 'stretch' }}>
        {offertes.map((d, i) => (
          <CarteChoix
            key={d.k} look="destination" on={destination === d.k} icon={d.icon} title={t(d.title)} note={t(d.note)}
            roving={rovingRadio({
              index: i, count: offertes.length, checked: destination === d.k, anyChecked: true,
              onPick: (n) => onDestination(offertes[n]!.k),
            })}
            onPick={() => onDestination(d.k)}
          />
        ))}
      </div>
      <div style={{ marginTop: 12, fontSize: 'var(--t-body)', color: 'var(--ink-600)' }}>
        <Trans k='Choisi : <1>{v}</1>' vars={{ v: t(choisi.title) }} parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} />
      </div>
      {avecQuantites5 && destination !== 'ref' && (
        // ⚠️ « IGNORER » NE RETIENT QUE LA FICHE : la marchandise annoncée par le fichier arrive quand même (le serveur
        //    envoie les quantités des articles déjà en base) — taire l'écart ferait croire à un stock inchangé.
        <p data-testid="import-doublons-quantites" style={{ margin: '8px 0 0', fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>
          {t('Les articles déjà en base gardent leur fiche, mais leurs quantités du fichier partent aussi vers cette destination.')}
        </p>
      )}

      {avecDocument && !fournisseur.trim() && (
        <div style={{ marginTop: 16, padding: '12px 14px', borderRadius: 'var(--r-sm)', background: 'var(--amber-bg)', border: '1px solid var(--amber)' }}>
          <p style={{ margin: '0 0 9px', fontSize: 'var(--t-body)', color: 'var(--ink-800)' }}>{t('Un bon de livraison ou une commande a toujours un fournisseur : choisissez celui du fichier.')}</p>
          <Picker
            id="bo-import-fournisseur-dest" variant="field-inline" label={fieldLabel('supplier')} allLabel={t('Choisir un fournisseur…')}
            options={(fournisseurs ?? []).map((s) => ({ value: s, label: s }))}
            loading={fournisseurs === null && fournisseursKo === null}
            unavailable={fournisseursKo !== null} unavailableReason={fournisseursKo ?? undefined}
            value={fournisseur} onChange={onFournisseur}
          />
        </div>
      )}

      {avecDocument && fournisseur.trim() !== '' && (
        <div style={{ marginTop: 16, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
          <div role="tablist" aria-label={t('Document')} style={{ display: 'flex', padding: 4, gap: 4, background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)' }}>
            {modes.map((m, i) => (
              <button
                key={m.k} type="button" role="tab" aria-selected={mode === m.k}
                id={`bo-import-doc-tab-${m.k}`} aria-controls="bo-import-doc-panel"
                {...rovingTab({ index: i, count: modes.length, selected: mode === m.k, onPick: (n) => onMode(modes[n]!.k) })}
                onClick={() => onMode(m.k)}
                style={{
                  flex: 1, height: 36, border: 'none', borderRadius: 'var(--r-ctrl)', fontSize: 'var(--t-body)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer',
                  background: mode === m.k ? 'var(--paper)' : 'transparent', color: mode === m.k ? 'var(--blue-fg)' : 'var(--ink-500)',
                  boxShadow: mode === m.k ? '0 1px 3px rgba(15,20,25,.16)' : 'none',
                }}
              >{m.l}</button>
            ))}
          </div>
          <div role="tabpanel" id="bo-import-doc-panel" aria-labelledby={`bo-import-doc-tab-${mode}`}>
          {mode === 'new' && (
            <div style={{ padding: 16 }}>
              <label htmlFor="bo-import-numero" style={{ display: 'block', fontSize: 'var(--t-note)', fontWeight: 500, color: 'var(--ink-500)', marginBottom: 5 }}>{t('Numéro du document')}</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  // ⚠️ LA RÈGLE DE L'ÉDITEUR DE COMMANDES (`typedDocumentNumber`) : nettoyé à la frappe, jamais refusé au lancement.
                  id="bo-import-numero" value={numero} onChange={(e) => onNumero(typedDocumentNumber(e.target.value))}
                  placeholder={proposition ?? ''}
                  style={{
                    flex: 1, minWidth: 0, height: 40, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
                    background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-title)', outline: 'none', fontFamily: 'var(--font-mono)',
                  }}
                />
                <button
                  type="button" aria-disabled={proposition === null || undefined}
                  onClick={() => { if (proposition) onNumero(proposition); }}
                  style={{
                    height: 40, padding: '0 14px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)',
                    color: 'var(--ink-700)', fontSize: 'var(--t-body)', fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 7,
                    cursor: proposition ? 'pointer' : 'not-allowed',
                  }}
                >
                  <Icon name="sparkles" size={14} stroke={1.9} />
                  {t('Proposer')}
                </button>
              </div>
              <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-400)', marginTop: 7, lineHeight: 1.5 }}>
                <Trans
                  k='Saisissez le numéro du fournisseur, ou faites-en proposer un au format <1>FOURNISSEUR-JJMMAA</1>. Laissé vide, il est attribué ainsi.'
                  parts={[(x) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-600)' }}>{x}</span>]}
                />
              </span>
            </div>
          )}
          {mode === 'existing' && (
            <div style={{ padding: 12 }}>
              {documentsKo !== null ? (
                <p role="alert" style={{ margin: 0, padding: '10px 4px', fontSize: 'var(--t-body)', color: 'var(--danger-ink)' }}>{t('Les documents ouverts n’ont pas pu être lus : {motif}', { motif: documentsKo })}</p>
              ) : documents === null ? (
                <p style={{ margin: 0, padding: '10px 4px', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Lecture des documents ouverts…')}</p>
              ) : documents.length === 0 ? (
                <div style={{ padding: '22px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Aucun document ouvert pour ce fournisseur')}</div>
                  <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', marginTop: 4 }}>{t('Créez-en un nouveau, ou changez de fournisseur.')}</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)', padding: '2px 4px 9px' }}>{t('Documents ouverts — non entrés en stock')}</div>
                  <div role="radiogroup" aria-label={t('Documents ouverts — non entrés en stock')} style={{ display: 'flex', flexDirection: 'column', gap: 7, maxHeight: 212, overflowY: 'auto', overflowX: 'hidden', paddingRight: 2 }}>
                    {documents.map((d, i) => {
                      const on = documentChoisi === d.number;
                      return (
                        <button
                          key={d.number} type="button" role="radio" aria-checked={on}
                          {...rovingRadio({ index: i, count: documents.length, checked: on, anyChecked: documentChoisi !== '', onPick: (n) => onDocument(documents[n]!.number) })}
                          onClick={() => onDocument(d.number)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 12, padding: '11px 13px', width: '100%', textAlign: 'left', cursor: 'pointer',
                            border: `1.5px solid ${on ? 'var(--blue)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-sm)', background: on ? 'var(--blue-bg)' : 'var(--paper)',
                          }}
                        >
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-900)', flexShrink: 0 }}>{d.number}</span>
                          <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--t-body)', color: 'var(--ink-600)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.supplierLabel ?? ''}</span>
                          <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{t('{n} ligne', { n: d.lines })}</span>
                          <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-400)', flexShrink: 0 }}>{jourDuDocument(d.date)}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 10, padding: '0 4px', lineHeight: 1.5 }}>
                    {t('Les quantités s’ajoutent aux lignes déjà présentes. Un document déjà entré en stock n’apparaît pas ici — il n’est plus modifiable.')}
                  </div>
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      )}
      <Note>{t('Un bon de livraison n’entre rien en stock : il attend son contrôle. C’est le choix par défaut parce qu’un fichier fournisseur annonce une livraison, il ne la constate pas.')}</Note>
    </div>
  );
}

/**
 * ÉTAPE 5 — CE QUI SERA IMPORTÉ : l'analyse faite dans le navigateur, avant d'envoyer quoi que ce soit.
 * ⚠️ LES NUMÉROS DE LIGNE SONT CEUX DU TABLEUR (en-tête compris) : c'est là que l'opérateur corrige.
 */
function EtapeRapport({ analyse, destination, doublons, numero }: {
  analyse: ReturnType<typeof analyserImport>; destination: ImportDestinationKind; doublons: 'skip' | 'update'; numero: string;
}) {
  const clean = analyse.bad.length === 0 && analyse.dup.length === 0;
  const pct = analyse.read ? Math.round((analyse.rows.length / analyse.read) * 100) : 0;
  const choisi = DESTINATIONS.find((d) => d.k === destination)!;
  return (
    <div>
      <Chapeau titre={t('Ce qui sera importé')} note={t('Rien n’est écrit avant que vous lanciez l’import.')} />
      <div data-testid="import-analyse" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 16 }}>
        <Tuile titre={t('Lignes retenues')} tone="teal" count={([figure, legend]) => <>{figure(nombre(analyse.rows.length))}{legend(t('{pct} % du fichier', { pct }))}</>} />
        <Tuile titre={t('Lignes en erreur')} count={(l) => <Trans k='<1>{n}</1> <2>écartée</2>' vars={{ n: analyse.bad.length }} parts={l} />} />
        {/* ⚠️ La légende des doublons dit la RÈGLE appliquée, elle ne nomme pas ce qui est compté : elle ne s'accorde pas. */}
        <Tuile titre={t('Doublons du fichier')} count={([figure, legend]) => <>{figure(nombre(analyse.dup.length))}{legend(<Trans k='1<1>re</1> occurrence gardée' parts={[(x) => <sup>{x}</sup>]} />)}</>} />
      </div>
      {analyse.bad.length > 0 && (
        <div style={{ border: '1px solid var(--amber)', borderRadius: 'var(--r-sm)', overflow: 'hidden', marginBottom: 12 }}>
          <div style={{ padding: '10px 14px', background: 'var(--amber-bg)', borderBottom: '1px solid var(--amber)', fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-800)' }}>{t('Lignes écartées — détail')}</div>
          {analyse.bad.slice(0, 8).map((b) => (
            <div key={b.line} style={{ display: 'grid', gridTemplateColumns: '56px minmax(0,1fr) minmax(0,1fr)', gap: 12, padding: '9px 14px', borderBottom: '1px solid var(--ink-100)', fontSize: 'var(--t-body)', alignItems: 'center' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-400)', fontVariantNumeric: 'tabular-nums' }}>L{b.line}</div>
              <div style={{ color: 'var(--ink-800)', minWidth: 0 }}>{b.why}</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', color: 'var(--ink-500)', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.ex}</div>
            </div>
          ))}
          <div style={{ padding: '10px 14px', background: 'var(--ink-50)', fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>
            {analyse.bad.length > 8 ? t('… et {n} autres lignes écartées. Le rapport complet sera téléchargeable après l’import.', { n: analyse.bad.length - 8 }) : t('Le rapport complet sera téléchargeable après l’import.')}
          </div>
        </div>
      )}
      {analyse.dup.length > 0 && (
        <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)', fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-800)' }}>{t('Doublons internes au fichier')}</div>
          {analyse.dup.slice(0, 5).map((d) => (
            <div key={d.line} style={{ display: 'grid', gridTemplateColumns: '56px minmax(0,1fr)', gap: 12, padding: '9px 14px', borderBottom: '1px solid var(--ink-100)', fontSize: 'var(--t-body)', alignItems: 'center' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-400)', fontVariantNumeric: 'tabular-nums' }}>L{d.line}</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', color: 'var(--ink-600)', minWidth: 0 }}>{d.ex}</div>
            </div>
          ))}
        </div>
      )}
      {clean && (
        <div style={{ padding: '14px 16px', borderRadius: 'var(--r-sm)', background: 'var(--teal-100)', border: '1px solid var(--teal-300)', fontSize: 'var(--t-lead)', color: 'var(--teal-800)' }}>
          {t('Aucune anomalie détectée — le fichier passe entièrement.')}
        </div>
      )}
      {analyse.withoutBarcode > 0 && (
        <Note>{t('{n} ligne sans code-barres : rapprochée de l’article qui porte son code article, sa couleur et sa taille, sinon créée avec un code-barres interne du magasin.', { n: analyse.withoutBarcode })}</Note>
      )}
      <div style={{ marginTop: 14, padding: '13px 15px', borderRadius: 'var(--r-sm)', background: 'var(--blue-bg)', border: '1px solid var(--blue)', display: 'flex', gap: 11 }}>
        <span style={{ color: 'var(--blue-fg)', flexShrink: 0, display: 'flex', paddingTop: 1 }}>
          <Icon name="clock" size={17} stroke={1.9} />
        </span>
        <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-700)', lineHeight: 1.55 }}>
          {/* ⚠️ LA PHRASE DE LA MAQUETTE, sans « plusieurs dizaines de milliers de lignes » : un envoi est plafonné à
              5 000 lignes, l'annoncer promettrait ce que l'import refuse. */}
          <Trans
            k='L’import prend du temps sur un gros fichier. <1>Il se déroule en arrière-plan</1> : la fenêtre se ferme, vous continuez à travailler, et une notification vous prévient quand tout est intégré. Sa progression reste visible en bas à droite.'
            parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]}
          />
        </span>
      </div>
      <Note>
        <Trans
          k='Destination : <1>{dest}</1> · doublons existants : <2>{dup}</2>'
          vars={{ dest: numero ? `${t(choisi.title)} ${numero}` : t(choisi.title), dup: doublons === 'skip' ? t('ligne ignorée') : t('écrasement avec le fichier') }}
          parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]}
        />
      </Note>
    </div>
  );
}
